import React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryFiltersProps {
  nameFilter: string;
  onNameChange: (value: string) => void;
  statusFilter: 'all' | 'active' | 'inactive';
  onStatusChange: (value: 'all' | 'active' | 'inactive') => void;
  stockStatusFilter: 'all' | 'available' | 'low' | 'depleted';
  onStockStatusChange: (value: 'all' | 'available' | 'low' | 'depleted') => void;
}

const statusOptions: Array<'all' | 'active' | 'inactive'> = ['all', 'active', 'inactive'];
const stockStatusOptions: Array<'all' | 'available' | 'low' | 'depleted'> = ['all', 'available', 'low', 'depleted'];

export const InventoryFilters: React.FC<InventoryFiltersProps> = ({
  nameFilter,
  onNameChange,
  statusFilter,
  onStatusChange,
  stockStatusFilter,
  onStockStatusChange,
}) => {
  const { t } = useLanguage();
  const stockStatusLabel: Record<InventoryFiltersProps['stockStatusFilter'], string> = {
    all: t('forms.common.all'),
    available: t('inventory.table.status.available'),
    low: t('inventory.table.status.low'),
    depleted: t('inventory.table.status.depleted'),
  };
  const statusLabel: Record<InventoryFiltersProps['statusFilter'], string> = {
    all: t('forms.common.all'),
    active: t('forms.common.statusActive'),
    inactive: t('forms.common.statusInactive'),
  };

  return (
  <div className="flex flex-col sm:flex-row flex-wrap gap-4 bg-white p-4 rounded-lg shadow-sm">
    <div className="flex-1">
      <label htmlFor="nameFilter" className="block text-sm font-medium text-gray-700">
        {t('purchasing.sampleList.filters.search')}
      </label>
      <input
        data-test-id="inventory-name-filter"
        type="text"
        id="nameFilter"
        placeholder={t('inventory.filters.searchNameOrCode')}
        value={nameFilter}
        onChange={(e) => onNameChange(e.target.value)}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
      />
    </div>
    <div className="w-full sm:w-40">
      <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
      <Listbox value={statusFilter} onChange={onStatusChange}>
        <div className="relative mt-1">
          <ListboxButton
            data-test-id="inventory-status-filter"
            className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
          >
            <span className="block truncate capitalize">{statusLabel[statusFilter]}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions className="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {statusOptions.map((status) => (
              <ListboxOption
                key={status}
                value={status}
                data-test-id={`inventory-status-option-${status}`}
                className={({ focus }: { focus: boolean }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'
                  }`
                }
              >
                {({ selected }: { selected: boolean }) => (
                  <>
                    <span className={`block truncate capitalize ${selected ? 'font-medium' : 'font-normal'}`}>
                      {statusLabel[status]}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>

    <div className="w-full sm:w-52">
      <label className="block text-sm font-medium text-gray-700">{t('inventory.table.columns.stockStatus')}</label>
      <Listbox value={stockStatusFilter} onChange={onStockStatusChange}>
        <div className="relative mt-1">
          <ListboxButton
            data-test-id="inventory-stock-status-filter"
            className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
          >
            <span className="block truncate">{stockStatusLabel[stockStatusFilter]}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions className="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {stockStatusOptions.map((status) => (
              <ListboxOption
                key={status}
                value={status}
                data-test-id={`inventory-stock-status-option-${status}`}
                className={({ focus }: { focus: boolean }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'
                  }`
                }
              >
                {({ selected }: { selected: boolean }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                      {stockStatusLabel[status]}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  </div>
  );
};
